/**
 * The goods block (economy plan SY-2a, D-TBD-c3; first built as the relay parts of M3 plan D-TBD 40). The hold
 * carries M2's six raw wares in `cargo.wares` and every other ware as a **good** in the optional `cargo.parts`
 * block (the key keeps its old name, so saves from before the goods load unchanged with no migration). Together
 * the two cover all 17 strategic wares.
 *
 * - **The goods list** is derived, never hand-written: the strategic `WARE_IDS` minus the raw tier, in strategic
 *   order (so a new non-raw strategic ware, such as the planned alloy, joins automatically), followed by the
 *   career-only goods (`CAREER_ONLY_GOOD_IDS`, append-only): `rush_spares` (MC-h3), which no strategic market
 *   trades or stocks (`isStrategicGoodId` is false; the strategic market quotes it `null` and refuses it).
 * - **Quantities are floats**, in units of `cargo-parts.json massT` tonnes (all 1 t now, so units = tonnes, the
 *   strategic market's unit). An absent key or an absent block means 0.
 * - **Relay parts** (`RELAY_PART_IDS` = M3's `PartId`) are the three goods the Reach relink delivers in whole
 *   units. A `RelayPartId` is a `GoodId`; the pre-goods aliases (`CargoPartId`, `PART_MASS_T`, `partCount`, ...) are
 *   retired (MC-b1), so every reader uses the goods names.
 * - **Value:** `GOOD_BASE_PRICE` holds every good: a strategic good at the strategic base price, Rush spares at
 *   `career.json rushSpares.base` (pod value, `cargoValue` and the death pod).
 * - **Rollback risk:** the save schema refuses unknown keys, so a save carrying a good added after this build
 *   does not load in this build (a career holding `rush_spares` is refused by a build older than MC-h3).
 *
 * Pure helpers only; the relay add and remove commands are in cargo.ts, the primitive `withGood` in ops.ts.
 */
import { z } from 'zod'
import careerJson from '../data/career.json'
import partsJson from '../data/cargo-parts.json'
import { careerFileSchema } from '../data/m2Schemas'
import strategicWaresJson from '../data/strategic/wares.json'
import { waresFileSchema } from '../strategic/schemas'
import { RAW_WARE_IDS, WARE_IDS as STRATEGIC_WARE_IDS, type RawWareId, type WareId as StrategicWareId } from '../strategic/wares'

/** A strategic ware that is not raw: carried as a good. */
export type StrategicGoodId = Exclude<StrategicWareId, RawWareId>

function isStrategicGood(id: StrategicWareId): id is StrategicGoodId {
  return !(RAW_WARE_IDS as readonly string[]).includes(id)
}

/** The non-raw strategic wares, in strategic order (derived from the strategic `WARE_IDS`). */
export const STRATEGIC_GOOD_IDS: readonly StrategicGoodId[] = Object.freeze(STRATEGIC_WARE_IDS.filter(isStrategicGood))

/** Goods only the career knows (no strategic ware). Append-only; `rush_spares` is MC-h3's. */
export const CAREER_ONLY_GOOD_IDS = ['rush_spares'] as const
export type CareerOnlyGoodId = (typeof CAREER_ONLY_GOOD_IDS)[number]

export type GoodId = StrategicGoodId | CareerOnlyGoodId

/** True for a good the strategic market trades (a non-raw strategic ware); false for a career-only good. */
export function isStrategicGoodId(v: GoodId): v is StrategicGoodId {
  return (STRATEGIC_GOOD_IDS as readonly string[]).includes(v)
}

/** True for a career-only good (no strategic ware: never quoted, traded or stocked by the strategic market). */
export function isCareerOnlyGoodId(v: unknown): v is CareerOnlyGoodId {
  return typeof v === 'string' && (CAREER_ONLY_GOOD_IDS as readonly string[]).includes(v)
}

/** Every good: the strategic goods in strategic order, then the career-only goods. */
export const GOOD_IDS: readonly [GoodId, ...GoodId[]] = (() => {
  const ids: GoodId[] = [...STRATEGIC_GOOD_IDS, ...CAREER_ONLY_GOOD_IDS]
  const first = ids[0]
  if (first === undefined) throw new Error('parts: the goods list is empty')
  const out: readonly [GoodId, ...GoodId[]] = [first, ...ids.slice(1)]
  return Object.freeze(out)
})()

export const goodIdSchema = z.enum(GOOD_IDS)

export function isGoodId(v: unknown): v is GoodId {
  return typeof v === 'string' && (GOOD_IDS as readonly string[]).includes(v)
}

/** Goods in the hold: quantity per good (float, never negative); an absent key is 0. */
export type Goods = Partial<Record<GoodId, number>>
/** The save mirror of `Goods`: unknown goods and negative or non-finite quantities fail. */
export const goodsSchema = z.partialRecord(goodIdSchema, z.number().nonnegative())

/** The three relay parts (M3 `PartId`, in its order); a subset of `GOOD_IDS`. */
export const RELAY_PART_IDS = ['hull_plating', 'electronics', 'drive_parts'] as const satisfies readonly GoodId[]
export type RelayPartId = (typeof RELAY_PART_IDS)[number]

/** True for one of the three relay parts (false for every other good). */
export function isRelayPartId(v: unknown): v is RelayPartId {
  return typeof v === 'string' && (RELAY_PART_IDS as readonly string[]).includes(v)
}

export const cargoPartsFileSchema = z.strictObject({
  comment: z.string().optional(),
  /** Tonnes per unit of every good (exhaustive: a missing good fails). */
  massT: z.record(goodIdSchema, z.number().positive()),
})
export type CargoPartsConfig = z.infer<typeof cargoPartsFileSchema>

/** Parses and validates cargo-parts.json (throws a ZodError naming the bad field). */
export function loadCargoParts(raw: unknown = partsJson): CargoPartsConfig {
  return cargoPartsFileSchema.parse(raw)
}

/** Tonnes per unit of each good (the bundled data). */
export const GOOD_MASS_T: Readonly<Record<GoodId, number>> = Object.freeze(loadCargoParts().massT)

/** Credits per Rush spare (`career.json rushSpares.base`; 0 when the block is absent). */
export const RUSH_SPARES_BASE: number = careerFileSchema.parse(careerJson).rushSpares?.base ?? 0

/**
 * Credits per unit of each good: a strategic good at the strategic base price (`strategic/wares.json basePrice`),
 * Rush spares at `RUSH_SPARES_BASE`.
 */
export const GOOD_BASE_PRICE: Readonly<Record<GoodId, number>> = (() => {
  const out = { rush_spares: RUSH_SPARES_BASE } as Record<GoodId, number>
  for (const w of waresFileSchema.parse(strategicWaresJson).wares) if (isStrategicGood(w.id)) out[w.id] = w.basePrice
  return Object.freeze(out)
})()

/** An empty goods block. */
export function emptyParts(): Goods {
  return {}
}

/** Quantity of `id` in a goods block (0 when the block or the key is absent). */
export function goodT(goods: Readonly<Goods> | undefined, id: GoodId): number {
  return goods?.[id] ?? 0
}

/** Tonnes of the goods in a block (0 when the block is absent). */
export function goodsT(goods: Readonly<Goods> | undefined): number {
  if (goods === undefined) return 0
  let t = 0
  for (const g of GOOD_IDS) t += (goods[g] ?? 0) * GOOD_MASS_T[g]
  return t
}

/** Credits of the goods in a block at `GOOD_BASE_PRICE` (Rush spares included). */
export function goodsBaseValue(goods: Readonly<Goods> | undefined): number {
  if (goods === undefined) return 0
  let v = 0
  for (const g of GOOD_IDS) v += (goods[g] ?? 0) * GOOD_BASE_PRICE[g]
  return v
}
